/**
 * Funzioni pure della posta in ingresso (Support Hub).
 *
 * Etichette, colori e formattazioni del registro "Posta supporto" e del box
 * "Email originale" nel Centro Ticket. Nessuna dipendenza da React o dalla rete:
 * si testano da sole (`__tests__/supportComponents.test.ts`).
 */

import type { BadgeVariant } from './constants';

/** Esiti del poller, nell'ordine in cui compaiono nei filtri e nei chip. */
export const INBOUND_STATUSES = ['created', 'appended', 'skipped', 'dry_run', 'error'] as const;

/** Solo questi esiti si possono rilanciare: created/appended hanno gia un ticket. */
export const REPROCESSABLE_STATUSES = ['error', 'skipped', 'dry_run'];

const STATUS_LABELS: Record<string, string> = {
  created: 'Ticket creato',
  appended: 'Aggiunta a ticket',
  skipped: 'Scartata',
  error: 'Errore',
  dry_run: 'Prova (nessun ticket)',
};

const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  created: 'success',
  appended: 'info',
  skipped: 'secondary',
  error: 'danger',
  dry_run: 'warning',
};

/** Classe badge V2 (`a2-badge--*`) per l'esito. */
const STATUS_A2: Record<string, string> = {
  created: 'a2-badge--green',
  appended: 'a2-badge--info',
  skipped: 'a2-badge--chip',
  error: 'a2-badge--dang',
  dry_run: 'a2-badge--warn',
};

/** Etichetta leggibile dell'esito; ricade sul valore grezzo se sconosciuto. */
export const inboundStatusLabel = (status?: string): string =>
  (status && STATUS_LABELS[status]) || status || '—';

export const inboundStatusVariant = (status?: string): BadgeVariant =>
  (status && STATUS_VARIANTS[status]) || 'secondary';

export const inboundStatusBadgeClass = (status?: string): string =>
  (status && STATUS_A2[status]) || 'a2-badge--chip';

/** Come e arrivato il messaggio. */
export const inboundKindLabel = (kind?: string): string => {
  switch (kind) {
    case 'direct':
      return 'Diretta';
    case 'forwarded_eml':
      return 'Inoltro (.eml)';
    case 'forwarded_inline':
      return 'Inoltro nel testo';
    default:
      return kind || '—';
  }
};

/**
 * Message-Id accorciato per la UI: e lungo e illeggibile, ma serve alla diagnosi.
 * Si mostra troncato (title con il valore intero). Le parentesi angolari degli
 * header vengono tolte perche non aggiungono informazione.
 */
export const truncateMessageId = (messageId?: string, max = 24): string => {
  const clean = String(messageId || '').trim().replace(/^<|>$/g, '');
  if (!clean) return '';
  if (clean.length <= max) return clean;
  // Il troncamento include i puntini: la stringa resa non supera mai `max`.
  return `${clean.slice(0, Math.max(1, max - 1))}…`;
};

/**
 * Data relativa breve ("3 min fa", "2 ore fa"). Usata per l'ultimo giro del
 * poller, dove conta l'ordine di grandezza, non il timestamp preciso.
 */
export const relativeTime = (iso?: string | null, now: Date = new Date()): string => {
  if (!iso) return 'mai';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'mai';
  const sec = Math.round((now.getTime() - t) / 1000);
  if (sec < 0) return 'tra poco';
  if (sec < 60) return 'pochi secondi fa';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min fa`;
  const ore = Math.round(min / 60);
  if (ore < 24) return `${ore} or${ore === 1 ? 'a' : 'e'} fa`;
  const gg = Math.round(ore / 24);
  return `${gg} giorn${gg === 1 ? 'o' : 'i'} fa`;
};

/** Lunghezza massima di una voce della lista "mittenti ignorati" (come il backend). */
export const IGNORE_PATTERN_MAX = 120;

/** Numero massimo di voci accettate dal backend (`MAX_PATTERNS`): oltre, la PUT e 400. */
export const IGNORE_PATTERN_MAX_ENTRIES = 500;

/** Le tre forme ammesse, mostrate come help text sotto il campo. */
export const IGNORE_PATTERN_HINT =
  'Forme ammesse: indirizzo esatto (avvisi@example.it), intero dominio (*@example.it, anche i sottodomini con *@*.example.it) oppure una parte locale su qualunque dominio (promo@*).';

/** Normalizzazione identica al backend: trim + minuscolo. */
export const normalizeIgnorePattern = (value?: string): string =>
  String(value || '').trim().toLowerCase();

/**
 * Forma ammessa di una voce: la STESSA regex del backend
 * (`backend/utils/supportIgnoredSenders.js`, `PATTERN_RX`).
 */
export const IGNORE_PATTERN_RX = /^(\*@\*?\.?[^@\s*]+|[^@\s*]+@\*|[^@\s*]+@[^@\s*]+)$/;

/**
 * Validazione della singola voce prima dell'invio: forma riconosciuta e
 * lunghezza sotto il limite. Il backend rivalida comunque e risponde 400
 * elencando le voci rifiutate.
 */
export const isValidIgnorePattern = (value?: string): boolean => {
  const p = normalizeIgnorePattern(value);
  return !!p && p.length <= IGNORE_PATTERN_MAX && IGNORE_PATTERN_RX.test(p);
};
